// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Modal from '@mui/material/Modal'
import OutlinedInput from '@mui/material/OutlinedInput'
import Paper from '@mui/material/Paper'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { fetchPoolParticipants } from '@react/fetchers/PoolFetchers'
import { fetchRewardsPlan } from '@react/fetchers/rewardsFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { PoolBaseOverviewCTO } from '@shared/PoolBaseCTO'
import { RewardsPlanCTO } from '@shared/RewardsPlanCTO'
import { EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'
import dayjs from 'dayjs'
import { ethers, ZeroAddress } from 'ethers'
import React, { useEffect, useState } from 'react'
import { CompanyProfileAddressItem } from './CompanyProfileAddressItem'
import RewardCard from './RewardsCard'
import RewardsEditForm from './RewardsEditForm'
const modalBoxSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
}
// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export default function RewardsForPoolView(props: { poolAddr: EthAddr }): React.ReactElement {
  const { poolAddr } = props

  const [rewardsPlans, setRewardsPlans] = useState<RewardsPlanCTO[]>([])
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
    chainConfiguration: { providerReadOnly },
    cognitoUser,
  } = useOtChainState()
  const poolOverview = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ZeroAddress]
  ) as PoolBaseOverviewCTO

  const ALL_PLANS = 'ALL_PLANS'
  const [openAddPlanModal, setOpenAddPlanModal] = React.useState<boolean>(false)
  useEffect(() => {
    if (providerReadOnly && poolAddr && serverApiBaseUrl && restAPIKey) {
      fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_participants) => {
          logOT.info('all users', _participants)
          setParticipants(_participants)
        })
        .catch((error) => {
          logOT.error(error)
        })
    }
  }, [providerReadOnly, poolAddr])

  const [participants, setParticipants] = React.useState<PoolAccountAccessPair[]>([])
  const [selectedParticipant, setSelectedParticipant] = React.useState<EthAddr>(ALL_PLANS)

  const [editPlanUuid, setEditPlanUuid] = React.useState<string>('')

  async function fetchAndStoreRewardsPlans() {
    fetchRewardsPlan(serverApiBaseUrl, restAPIKey, cognitoUser, poolAddr).then((rewardsPlans: RewardsPlanCTO[]) => {
      logOT.info('rewardsPlans', rewardsPlans)
      setRewardsPlans(rewardsPlans)
      setEditPlanUuid('')
    })
  }
  useEffect(() => {
    fetchAndStoreRewardsPlans().catch((error) => {
      logOT.error('error', error)
    })
  }, [poolAddr])

  const editPlan = rewardsPlans.find((rewardsPlan) => rewardsPlan.uuid === editPlanUuid)

  logOT.info('rewardsPlans', rewardsPlans)
  logOT.info('editPlan', editPlan)
  return (
    <Paper>
      <Box
        data-testid="rewards-for-pool-view"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <Button
            onClick={() => {
              fetchAndStoreRewardsPlans().catch((error) => {
                logOT.error('error', error)
              })
            }}
          >
            Refresh
          </Button>
          <FormControl sx={{ m: 1, width: '80%' }}>
            <InputLabel id="asset-label">Lender</InputLabel>
            <Select
              labelId="asset-label"
              id="asset-select"
              input={<OutlinedInput id="asset-select" label="Asset" />}
              value={selectedParticipant}
              renderValue={(value: string) => {
                if (value === ALL_PLANS) {
                  return (
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      All Plans
                    </Typography>
                  )
                }
                if (value === ZeroAddress) {
                  return (
                    <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                      Default rewards for all lenders
                    </Typography>
                  )
                }
                return <CompanyProfileAddressItem walletAddr={value} />
              }}
              onChange={(event: SelectChangeEvent) => {
                logOT.info('event.target.value', event.target.value)
                setSelectedParticipant(event.target.value as string)
              }}
            >
              <MenuItem
                value={ALL_PLANS}
                key={ALL_PLANS}
                style={{
                  paddingLeft: 12,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    borderWidth: 1,
                    borderColor: 'grey.500',
                    borderStyle: 'solid',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    padding: 6,
                  }}
                >
                  <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                    All Plans
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem
                value={ZeroAddress}
                key={`asset-option-${ZeroAddress}`}
                style={{
                  paddingLeft: 12,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    borderWidth: 1,
                    borderColor: 'grey.500',
                    borderStyle: 'solid',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    padding: 6,
                  }}
                >
                  <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                    Default rewards for all lenders
                  </Typography>
                </Box>
              </MenuItem>
              {participants
                // .filter((p: PoolAccountAccessPair) => rewardsPlans.find((rp) => rp.accountAddr === p.accountAddr))
                .map((p: PoolAccountAccessPair) => (
                  <MenuItem
                    value={p.accountAddr}
                    key={`asset-option-${p.accountAddr}`}
                    style={{
                      paddingLeft: 12,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <CompanyProfileAddressItem hasBorder={true} walletAddr={p.accountAddr} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setEditPlanUuid('')
              setOpenAddPlanModal(true)
              // const uuid = ethers.uuidV4(ethers.randomBytes(16))
              // setEditPlanUuid(uuid)

              // setRewardsPlans([
              //   ...rewardsPlans,
              //   {
              //     uuid: uuid,
              //     poolAddr: poolAddr,
              //     accountAddr: selectedParticipant,
              //     rewardsPlanRows: [],
              //     timestamp: Date.now(),
              //     currencyLabel: poolOverview.currencyLabel,
              //     poolDisplayName: poolOverview.name,
              //     companyName: toCompanyAddrString(selectedParticipant, companyProfiles).display,
              //     poolAddrLenderAddr: `${poolAddr}-${selectedParticipant}`,
              //     startDateString: dayjs().format('YYYY-MM-DD'),
              //     endDateString: dayjs().add(30, 'days').format('YYYY-MM-DD'),
              //   },
              // ])
            }}
          >
            Add Rewards for an Account
          </Button>
          {openAddPlanModal && (
            <Modal
              open={true}
              onClose={() => {
                setEditPlanUuid('')
                setOpenAddPlanModal(false)
              }}
            >
              <Paper sx={modalBoxSx}>
                <RewardsEditForm
                  participants={participants}
                  existingPlans={rewardsPlans}
                  rewardsPlan={{
                    uuid: ethers.uuidV4(ethers.randomBytes(16)),
                    poolAddr: poolAddr,
                    // accountAddr: selectedParticipant,
                    rewardsPlanRows: [],
                    timestamp: Date.now(),
                    currencyLabel: poolOverview.currencyLabel,
                    poolDisplayName: poolOverview.name,
                    // companyName: toCompanyAddrString(selectedParticipant, companyProfiles).display,
                    // poolAddrLenderAddr: `${poolAddr}-${selectedParticipant}`,
                    startDateString: dayjs().format('YYYY-MM-DD'),
                    endDateString: dayjs().add(30, 'days').format('YYYY-MM-DD'),
                  }}
                  onSave={() => {
                    setOpenAddPlanModal(false)
                    fetchAndStoreRewardsPlans().catch((error) => {
                      logOT.error('error', error)
                    })
                  }}
                />
              </Paper>
            </Modal>
          )}
        </Box>
        <Box
          data-testid="rewards-for-pool-view-box"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '100px',
            width: '100%',
          }}
        >
          {editPlan && (
            <Modal open={true} onClose={() => setEditPlanUuid('')}>
              <Paper sx={modalBoxSx}>
                <RewardsEditForm
                  participants={participants}
                  key={`${editPlan.uuid}`}
                  existingPlans={rewardsPlans}
                  rewardsPlan={editPlan}
                  onSave={() => {
                    setEditPlanUuid('')
                    fetchAndStoreRewardsPlans().catch((error) => {
                      logOT.error('error', error)
                    })
                  }}
                />
              </Paper>
            </Modal>
          )}
          {rewardsPlans
            .filter((rewardPlan) => selectedParticipant == ALL_PLANS || rewardPlan.accountAddr == selectedParticipant)
            .map((rewardsPlan) => (
              <RewardCard
                key={`${rewardsPlan.uuid}`}
                rewardsPlan={rewardsPlan}
                onEdit={() => setEditPlanUuid(rewardsPlan.uuid)}
              />
            ))}
        </Box>
      </Box>
    </Paper>
  )
}
